import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Typography } from '@mui/material';
import { Flag } from '@mui/icons-material';

import { CatalogEntry, CatalogFreshness } from '../queries';
import {
  catalogCityChain,
  catalogFreshnessLines,
  catalogMeta,
} from '../circuits';
import { useAudience } from '../audience';
import { MetaLine } from './MetaLine';
import { PriceBlock } from './PriceBlock';
import { ResultMedia } from './ResultMedia';
import { RuteroResultCard } from './RuteroResultCard';

interface Props {
  results: CatalogEntry[];
  total: number;
  catalog?: CatalogFreshness[];
  /**
   * Proveedores que rastrean catálogo pero NO cotizan salidas en esta agencia
   * (sin conector configurado para `circuits`). La card lo dice: el catálogo
   * los publica igual, y descubrirlo recién al pedir las salidas era el
   * callejón sin salida que arregla este estado.
   */
  unsearchableProviders?: string[];
  onSelect: (product: CatalogEntry) => void;
}

/**
 * La cadena de ciudades: `meta.cities` A SECAS. El catálogo no trae itinerario,
 * así que acá no hay noches por ciudad — esas se derivan del día a día de la
 * ficha. Las flechas van del gris de los separadores: lo que se lee son las
 * ciudades. Una sola línea: la cadena puede tener cuarenta ciudades y la
 * densidad de la lista es criterio de aceptación.
 */
function CityChain({ cities, truncated }: { cities: string[]; truncated: boolean }) {
  return (
    <Typography
      noWrap
      // El catálogo publica los nombres normalizados en minúscula ("paris"):
      // los acentos no se pueden recuperar, la capitalización sí.
      sx={{
        fontSize: 13,
        fontWeight: 500,
        lineHeight: 1.35,
        textTransform: 'capitalize',
      }}
      title={`${cities.join(' → ')}${truncated ? ' …' : ''}`}
    >
      {cities.map((city, index) => (
        <Fragment key={`${city}-${index}`}>
          {index > 0 && (
            <Box component="span" color="text.disabled" sx={{ mx: 0.875, fontSize: 11 }}>
              →
            </Box>
          )}
          {city}
        </Fragment>
      ))}
      {truncated && (
        <Box component="span" color="text.disabled" sx={{ ml: 0.875 }}>
          …
        </Box>
      )}
    </Typography>
  );
}

/**
 * La lista de DESCUBRIMIENTO: qué circuitos existen que pasen por esas
 * ciudades y duren eso. Es el arquetipo de PRODUCTO como el resto de las
 * verticales, con una particularidad: la media no es una foto, es el MAPA del
 * recorrido que publica el proveedor (`meta.images[0]`). No hay coordenadas en
 * el contrato, así que un mapa interactivo sería inventar datos; el del
 * proveedor viene dado y es el mejor argumento de venta que tiene la card.
 *
 * El precio que muestra es un "desde" del catálogo rastreado, no una
 * cotización: no hay token de provenance detrás y por eso no se puede reservar
 * desde acá. Va rotulado como indicativo pegado al número — es el mismo "desde"
 * que publican Europamundo y VPT en sus propios buscadores, y sin él la
 * pantalla quedaba por debajo de la experiencia que el asesor ya conoce. El
 * precio firme aparece al abrir el circuito, con las salidas cotizadas.
 *
 * Por eso mismo el CTA nunca se deshabilita: no hay cotización que se venza.
 */
export function CircuitCatalogResults({
  results,
  total,
  catalog,
  unsearchableProviders = [],
  onSelect,
}: Props) {
  const { t, i18n } = useTranslation();
  const { isAgent } = useAudience();

  const freshness = catalogFreshnessLines(catalog, i18n.language);

  return (
    <Box display="flex" flexDirection="column" gap={1.5}>
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.circuits.catalogFound', { count: total })}
        </Typography>
        {/* De cuándo es el catálogo de cada proveedor, y el hueco CONOCIDO de
            cobertura: G Adventures no publica catálogo listable, así que no
            participa de la búsqueda por destino. Decirlo evita que un asesor
            que sabe que existe lo reporte como bug — pero es una condición
            permanente del vertical, no una novedad: texto quieto, no un Alert
            fijo que hay que aprender a ignorar. */}
        <MetaLine
          items={[
            freshness.length > 0 && t('travelServices.circuits.catalogTracked'),
            ...freshness.map((line) =>
              t('travelServices.circuits.catalogProviderFreshness', {
                provider: line.provider,
                total: line.total,
                count: line.count,
                date: line.date,
              })),
            <Box key="coverage" component="span" color="text.disabled">
              {t('travelServices.circuits.catalogCoverage')}
            </Box>,
          ]}
        />
      </Box>

      {results.length === 0 ? (
        <Alert severity="warning">
          {t('travelServices.circuits.noCatalogResults')}
        </Alert>
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {results.map((entry, index) => {
            const meta = catalogMeta(entry);
            const { cities, truncated } = catalogCityChain(entry);
            const unsearchable = Boolean(
              entry.provider && unsearchableProviders.includes(entry.provider),
            );
            return (
              <RuteroResultCard
                key={`${entry.provider}-${entry.id}`}
                index={index}
                media={
                  <ResultMedia
                    photo={meta.images?.[0]}
                    icon={<Flag sx={{ fontSize: 44 }} color="action" />}
                    alt={entry.name}
                  />
                }
                header={
                  // El proveedor es PROCEDENCIA, no argumento de venta: chico,
                  // gris y solo para el asesor, igual que en las otras ocho.
                  isAgent && entry.provider ? (
                    <Typography component="span" color="text.disabled" sx={{ fontSize: 11.5 }}>
                      {entry.provider}
                    </Typography>
                  ) : undefined
                }
                title={
                  <Typography
                    sx={{ fontSize: 15.5, fontWeight: 600, lineHeight: 1.35 }}
                  >
                    {entry.name}
                  </Typography>
                }
                subtitle={
                  cities.length > 0 ? (
                    <CityChain cities={cities} truncated={truncated} />
                  ) : undefined
                }
                details={
                  // Lo único que el catálogo trae del producto además de la
                  // cadena. Nada de hoteles ni régimen de comidas: no vienen.
                  // Y, cuando el proveedor no cotiza acá, POR QUÉ este
                  // circuito no va a tener salidas — antes del click, no
                  // después de una búsqueda que iba a fallar igual.
                  meta.durationDays || unsearchable ? (
                    <MetaLine
                      items={[
                        meta.durationDays &&
                          t('travelServices.durationDays', {
                            count: meta.durationDays,
                          }),
                        unsearchable && (
                          <Box key="unsearchable" component="span" color="warning.dark">
                            {t('travelServices.circuits.providerNotSearchableShort', {
                              provider: entry.provider,
                            })}
                          </Box>
                        ),
                      ]}
                    />
                  ) : undefined
                }
                price={
                  meta.pricing ? (
                    <PriceBlock
                      pricing={meta.pricing}
                      fromLabel={t('travelServices.priceFrom')}
                      unit={t('travelServices.circuits.perPersonDouble')}
                      notes={
                        // Del catálogo NUNCA sale un precio firme (es una
                        // propiedad del endpoint, no de la entrada): el total
                        // depende de la salida y sale del search en vivo.
                        <Box component="span" sx={{ color: 'warning.dark', fontWeight: 600 }}>
                          {t('travelServices.circuits.indicativeByDeparture')}
                        </Box>
                      }
                    />
                  ) : (
                    // Mientras el proveedor termina de llenar su catálogo hay
                    // circuitos sin "desde": la card degrada a texto, no
                    // inventa un precio ni desaparece.
                    <Typography variant="caption" color="text.secondary">
                      {t('travelServices.circuits.priceOnDepartures')}
                    </Typography>
                  )
                }
                action={
                  <Button
                    variant="contained"
                    sx={{ minWidth: 104, height: 30 }}
                    onClick={() => onSelect(entry)}
                  >
                    {t('travelServices.circuits.seeCircuit')}
                  </Button>
                }
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
